/**
 * Update Work Order Status API Contract
 *
 * ts-rest contract definition for updating the work order status of a single job.
 *
 * Endpoint  : PATCH /jobs/work-order/:id/status
 * Method    : PATCH
 * Auth      : Required (standard bearer token via middleware)
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

/**
 * Request body schema – the new work order status ID to apply
 */
export const updateWorkOrderStatusBodySchema = z.object({
  work_order_status_id: z.number().int(),
});

/**
 * Response schema – job id, status fields after update
 */
export const updateWorkOrderStatusResponseSchema = z.object({
  id: z.number(),
  status: z.number().nullable(),
  work_order_status_id: z.number().nullable(),
  work_order_status_name: z.string().nullable(),
});

export type UpdateWorkOrderStatusBody = z.infer<typeof updateWorkOrderStatusBodySchema>;
export type UpdateWorkOrderStatusResponse = z.infer<typeof updateWorkOrderStatusResponseSchema>;

/**
 * Update Work Order Status Contract
 *
 * Base path: /jobs  (mounted in apps/backend/src/routes/index.ts via jobsRouter)
 */
export const updateWorkOrderStatusContract = c.router({
  updateWorkOrderStatus: {
    method: 'PATCH',
    path: '/work-order/:id/status',
    pathParams: z.object({
      id: z.coerce.number().int(),
    }),
    body: updateWorkOrderStatusBodySchema,
    responses: {
      200: updateWorkOrderStatusResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update work order status',
    description: 'Updates the work order status for a single job identified by its ID.',
  },
});
